/**
 * O que a equipe registra na conversa do caso (E149.F2 — GUS-149, ADR-0102 §3): uma mensagem ao
 * cliente, um pedido de documento (o documento, o motivo e, se quiser, a data para responder, que
 * só é informada: não vence nem bloqueia) e, depois da entrega, a ciência do cliente ("o cliente
 * disse que está ok"), que não aprova nem libera nada.
 */
import { useState } from 'react';

import { Field, Input, Seg, type SegOption } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import {
  LIMITES_DA_CONVERSA,
  type MensagemEntradaData,
  type RecusaDaConversa,
} from '@/lib/conversa_do_caso';

import { Compositor } from './Compositor';

const t = ptBR.conversaDoCaso;

type Escrita = 'mensagem' | 'pedido_de_material' | 'ciencia';
type Enviar = (entrada: MensagemEntradaData) => Promise<RecusaDaConversa | null>;

function opcoes(entregue: boolean): SegOption<Escrita>[] {
  const todas: Escrita[] = entregue
    ? ['mensagem', 'pedido_de_material', 'ciencia']
    : ['mensagem', 'pedido_de_material'];
  return todas.map((valor) => ({
    value: valor,
    label: t.equipe.escrever[valor],
    testId: `equipe-escrever-${valor}`,
  }));
}

function PedidoDeMaterial({ enviar }: { enviar: Enviar }) {
  const [material, setMaterial] = useState('');
  const [motivo, setMotivo] = useState('');
  const [prazo, setPrazo] = useState('');
  const extras = (
    <div className="grid gap-3 md:grid-cols-2">
      <Field label={t.equipe.pedidoMaterial} required>
        <Input
          value={material}
          maxLength={LIMITES_DA_CONVERSA.material}
          data-testid="equipe-pedido-material"
          onChange={(e) => setMaterial(e.target.value)}
        />
      </Field>
      <Field label={t.equipe.pedidoPrazo} description={t.equipe.pedidoPrazoNota}>
        <Input
          type="date"
          value={prazo}
          data-testid="equipe-pedido-prazo"
          onChange={(e) => setPrazo(e.target.value)}
        />
      </Field>
      <Field label={t.equipe.pedidoMotivo} required className="md:col-span-2">
        <Input
          value={motivo}
          maxLength={LIMITES_DA_CONVERSA.motivo}
          data-testid="equipe-pedido-motivo"
          onChange={(e) => setMotivo(e.target.value)}
        />
      </Field>
    </div>
  );
  return (
    <Compositor
      rotulo={t.equipe.pedidoTexto}
      obrigatorio={false}
      extras={extras}
      completo={material.trim() !== '' && motivo.trim() !== ''}
      testId="equipe-pedido"
      onEnviar={async (texto) => {
        const recusa = await enviar({
          tipo: 'pedido_de_material',
          material: material.trim(),
          motivo: motivo.trim(),
          responder_ate: prazo === '' ? null : prazo,
          texto,
        });
        if (recusa === null) {
          setMaterial('');
          setMotivo('');
          setPrazo('');
        }
        return recusa;
      }}
    />
  );
}

function Formulario({ escrita, enviar }: { escrita: Escrita; enviar: Enviar }) {
  if (escrita === 'pedido_de_material') return <PedidoDeMaterial enviar={enviar} />;
  if (escrita === 'ciencia') {
    return (
      <Compositor
        rotulo={t.equipe.cienciaTexto}
        obrigatorio={false}
        nota={t.equipe.cienciaNota}
        botao={t.equipe.escrever.ciencia}
        testId="equipe-ciencia"
        onEnviar={(texto) => enviar({ tipo: 'ciencia', texto })}
      />
    );
  }
  return (
    <Compositor
      rotulo={t.equipe.mensagemRotulo}
      obrigatorio
      testId="equipe-mensagem"
      onEnviar={(texto) => enviar({ tipo: 'mensagem', texto })}
    />
  );
}

export function EscreverNaEquipe({ entregue, enviar }: { entregue: boolean; enviar: Enviar }) {
  const [escolhida, setEscolhida] = useState<Escrita>('mensagem');
  const escrita = !entregue && escolhida === 'ciencia' ? 'mensagem' : escolhida;
  return (
    <section
      className="space-y-3 border border-border bg-surface p-4"
      data-testid="equipe-escrever"
    >
      <Seg
        label={t.compositor.tipoRotulo}
        options={opcoes(entregue)}
        value={escrita}
        onChange={setEscolhida}
      />
      <Formulario key={escrita} escrita={escrita} enviar={enviar} />
    </section>
  );
}
